import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useMemo, useState, useEffect } from 'react';
import Head from 'next/head';
import { REEBELO_CURRENCY } from '@lambda/reebelo';
import cn from 'classnames';
import {
  ProductGroup,
  PopularProductGroup,
} from '@lambda/apis/src/product-group/types';
import { ProductCategoryWarranty } from '@lambda/apis/src/category/types';
import { handleFreeReturn } from '@lambda/commons/helpers';
import FiltersModal from '@/components/collections/FiltersModal';
import Filters from '@/components/collections/Filters';
import { SearchResults, SearchKeys } from '@/lib/search-helpers';
import settings from '@/settings';
import i18n from '@/settings/i18n';
import { QsContext, useQs } from '@/lib/use-qs';
import InfiniteScrollSearchResults from './InfiniteScrollSearchResults';
import GTM from '@/lib/google-tag-manager';
import { replacePlusSymbol } from '../../pages/products/[handle]';

import { getShortenCountryName } from '@/lib/use-origin-country';
import { HEALTH_BEAUTY_CATEGORIES } from '@/settings/configs';
import SeoSection from '../collections-v2/common/SeoSection';
import { WebsiteSchema } from '../homepage/WebsiteSchema';
import {
  trackProductListFiltered,
  trackProductListViewed,
  trackProductsSearched,
} from '@/lib/analytics/trackProduct';
import { formatProductGroupAsLegacyCollectionSearch } from '@/lib/utils/helper';
import SortSelect from '@/components/collections/SortSelect';
import FilterSidebar from '@/components/collections/FilterSidebar';
import CollectionBanner from '../collections-v2/common/Collections/Banner';
import { sanitizeSearchQuery } from '../commons/Navbar/AccessBar/helpers';

const BackToTopButton = dynamic(
  () => import('@/components/collections/BackToTopButton'),
  { ssr: false },
);

const t = i18n.init();

const freeReturnDays = handleFreeReturn(
  settings.store,
  settings.free_return_days,
);

export default function SearchPage(
  props: SearchResults & {
    trackingPageTitle?: string;
    productGroup?: ProductGroup;
    popularProductGroups?: PopularProductGroup[];
    categoryWarrantyInfo?: ProductCategoryWarranty;
  },
) {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [qs, updateQs] = useQs<SearchKeys>();

  const router = useRouter();
  const handle = router.query.handle as string;
  const { productGroup, popularProductGroups, categoryWarrantyInfo } = props;
  const collectionTitle = productGroup?.name ?? '';
  const collectionCategories = productGroup?.filters?.categories ?? [];
  const collectionCategory = collectionCategories[0] || '';
  const search = productGroup
    ? formatProductGroupAsLegacyCollectionSearch(productGroup)
    : null;
  const isCollectionPage = !router.pathname.includes('search');
  const giftsPage = router.asPath.includes('gifts-guide');

  useEffect(() => {
    trackProductListViewed(props.offers);
    if (props.searchQuery) trackProductsSearched(props.searchQuery);
    if (qs) trackProductListFiltered(props.offers, qs);

    GTM.eventOffers('view_item_list', props.offers);

    const impressions = props.offers.map(
      (
        { title, key, productId, price, brand, categories, productType, color },
        index,
      ) => ({
        name: title,
        id: key || productId,
        price,
        brand,
        category:
          categories && categories.length ? categories[0] : productType || '',
        variant: color || '',
        list: 'Products Page',
        position: index + 1,
      }),
    );

    const eventPayload = {
      currencyCode: REEBELO_CURRENCY[settings.store],
      impressions,
    };

    GTM.event(null, eventPayload);
  }, [qs]);

  useEffect(() => {
    updateQs({
      query: props.searchQuery ?? search?.query,
      fullPhraseMatch: search?.fullPhraseMatch?.toString(),
      categories: search?.filters.categories,
    });
  }, []);

  const onPriceRangeChange = ({ min, max }: { min: number; max: number }) => {
    // we remove the min/max the url if it is out of bound
    const minPrice = min <= props.priceRange.min ? undefined : min.toString();
    const maxPrice = props.priceRange.max <= max ? undefined : max.toString();

    updateQs({
      minPrice,
      maxPrice,
    });
  };

  const query = sanitizeSearchQuery(router.query.query as string);
  const MemoSortSelect = useMemo(() => SortSelect, [query]);
  const isUSStore = settings.store === 'reebelo-us';

  const isHealthBeautyCollection =
    HEALTH_BEAUTY_CATEGORIES.includes(collectionCategory);

  // Handle Meta Tags
  const getCollectionMetaContent = () => {
    if (isHealthBeautyCollection) {
      return `Save big on Reebelo's widest collections of sustainable ${replacePlusSymbol(
        collectionTitle || 'products',
      )}. Free delivery. ${freeReturnDays} days free returns. ${
        settings.country
      }'s leading marketplace for sustainable products.`;
    }

    if (isUSStore) {
      return `Save big on Reebelo's widest collections of refurbished ${replacePlusSymbol(
        collectionTitle || 'products',
      )}. Free delivery. ${freeReturnDays} days free returns. Up to ${
        settings.warranty_month
      } months warranty. ${
        settings.country
      }'s leading marketplace for sustainable tech.`;
    }

    return `Save big on Reebelo's widest collections of refurbished ${replacePlusSymbol(
      collectionTitle || 'products',
    )}. Free delivery. ${freeReturnDays} days free returns. Up to ${
      settings.warranty_month
    } months warranty. ${
      settings.country
    }'s leading marketplace for sustainable tech.`;
  };

  const metaContent = isUSStore
    ? `Buy brand new or pre-loved refurbished electronics, phones and tablets online at Reebelo ${settings.country}. Free delivery. ${freeReturnDays} days free returns. 12 months warranty. ${settings.country}'s leading marketplace for sustainable tech.`
    : `Buy brand new or pre-loved refurbished electronics, phones and tablets online at Reebelo ${settings.country}. Free delivery. ${freeReturnDays} days free returns. 12 months warranty. ${settings.country}'s leading marketplace for sustainable tech.`;

  // Handle Page Title
  const getPreTitle = () => {
    if (giftsPage) return 'Search results for';
    if (isHealthBeautyCollection) return 'Sustainable';
    if (isUSStore) return `Refurbished`;

    return `Refurbished`;
  };

  const pageTitle = `${getPreTitle()} ${replacePlusSymbol(
    collectionTitle,
  )} - Best Prices in ${getShortenCountryName()}`;

  return (
    <QsContext.Provider value={[qs, updateQs]}>
      <Head>
        {isCollectionPage ? (
          <>
            <title>{pageTitle}</title>
            <WebsiteSchema />
            <meta name="description" content={getCollectionMetaContent()} />
          </>
        ) : (
          <>
            <title>{t`Search results for ${{
              query: (query || '').toString(),
            }}`}</title>

            <WebsiteSchema />
            <meta name="description" content={metaContent} />
          </>
        )}
      </Head>
      <BackToTopButton />
      <div className="bg-white">
        <CollectionBanner
          title={isCollectionPage ? collectionTitle : query}
          searchResult={!isCollectionPage || giftsPage}
          category={collectionCategory}
          categoryWarrantyInfo={categoryWarrantyInfo}
        />
        <div className="reebelo-container py-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 lg:gap-6 xl:grid-cols-4">
            {/* Filters */}
            <div className="hidden lg:block">
              <div className="rounded border bg-white">
                <FilterSidebar
                  handle={handle}
                  options={props.options}
                  query={query}
                  priceRange={props.priceRange}
                  qs={qs}
                  onPriceRangeChange={onPriceRangeChange}
                  id="default"
                />
              </div>
            </div>
            <div className="col-span-2 xl:col-span-3">
              <div
                className={cn('flex justify-center lg:mb-3 lg:justify-end', {
                  'lg:justify-between': props.offers.length === 0,
                })}
              >
                {props.offers.length === 0 && (
                  <>
                    {isCollectionPage && (
                      <h1 className="hidden">{collectionTitle}</h1>
                    )}
                    <h3 className="hidden items-center justify-start text-lg font-bold text-black lg:flex">
                      {t`Search results`}
                    </h3>
                  </>
                )}
                <MemoSortSelect className="hidden lg:block" />
              </div>
              <Filters setIsOpen={setIsFilterOpen} />
              <InfiniteScrollSearchResults
                key={router.asPath}
                offers={props.offers}
                hasNextPage={props.hasNextPage}
                isCollection={isCollectionPage}
                productGroup={productGroup}
                popularProductGroups={popularProductGroups}
              />
            </div>
          </div>
          <div className="mt-16">
            <SeoSection handle={handle} />
          </div>
        </div>
      </div>
      <FiltersModal
        handle={handle}
        options={props.options}
        query={query}
        priceRange={props.priceRange}
        qs={qs}
        onPriceRangeChange={onPriceRangeChange}
        isFilterOpen={isFilterOpen}
        setIsFilterOpen={setIsFilterOpen}
        id="modal"
      />
    </QsContext.Provider>
  );
}
